import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Save, Loader2, HelpCircle, Building2 } from "lucide-react";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Label } from "@/Components/ui/label";
import { Textarea } from "@/Components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/Components/ui/select";
import CNPJInput from "@/Components/ui/cnpj-input";
import { cleanCNPJ } from "@/utils/cnpjMask";
import { Partnership } from "@/Entities/Partnership";
import OptionsService from "@/Entities/OptionsService";
import { parseCurrencyValue } from "@/utils/index";
import { applyCurrencyMask, removeCurrencyMask, formatCurrencyForInput } from "@/utils/currencyMask";
import logger from '../../Entities/LoggerService';
import { useToast } from "@/Components/ui/use-toast";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

const PartnershipForm = ({ partnership, onSave, onCancel, isLoading }) => {
  const { toast } = useToast();
  useBodyScrollLock();
  const [formData, setFormData] = useState({
    processo_ano: '',
    tipo_parceria: '',
    ano: '',
    origem: '',
    analises_observacoes: '',
    ug: '',
    cnpj: '',
    osc: '',
    valor: '',
    data_inicio: '',
    data_fim: '',
    prazos: '', // Campo de prazo em dias (número)
    data_final_calculada: '', // Data calculada
    local_f: ''
    // status é calculado automaticamente pelo backend
  });

  const [errors, setErrors] = useState({});
  
  // Estados para opções dinâmicas
  const [tiposParceria, setTiposParceria] = useState([]);
  const [origens, setOrigens] = useState([]);
  const [prazosDisponiveis, setPrazosDisponiveis] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Carregar opções dinâmicas ao montar o componente
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const [tipos, origs, prazs] = await Promise.all([
          OptionsService.getTiposParceria(),
          OptionsService.getOrigens(),
          OptionsService.getPrazos()
        ]);
        setTiposParceria(tipos);
        setOrigens(origs);
        setPrazosDisponiveis(prazs);
      } catch (error) {
        logger.error('partnership', 'PartnershipForm.loadOptions', 'Erro ao carregar opções', {}, error);
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Erro ao carregar opções do formulário"
        });
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  // Preencher formulário quando partnership existir (modo edição)
  useEffect(() => {
    if (partnership) {
      setFormData({
        processo_ano: partnership.processo_ano || '',
        tipo_parceria: partnership.tipo_parceria || '',
        ano: partnership.ano || '',
        origem: partnership.origem || '',
        analises_observacoes: partnership.analises_observacoes || '',
        ug: partnership.ug || '',
        cnpj: partnership.cnpj || '',
        osc: partnership.osc || '',
        valor: partnership.valor ? formatCurrencyForInput(partnership.valor) : '',
        data_inicio: partnership.data_inicio || '',
        data_fim: partnership.data_fim || '',
        prazos: partnership.prazos || '',
        data_final_calculada: partnership.data_final_calculada || '',
        local_f: partnership.local_f || ''
      });
    }
  }, [partnership]);

  // Lógica condicional: Calcular data_final_calculada quando datas e prazo mudarem
  useEffect(() => {
    const { data_inicio, data_fim, prazos } = formData;
    
    // Cenário A: Ambas as datas preenchidas
    if (data_inicio && data_fim && prazos && prazos !== 'Não Calculado') {
      try {
        const dataFim = new Date(data_fim);
        const diasPrazo = parseInt(prazos);
        
        if (!isNaN(diasPrazo) && diasPrazo > 0) {
          // Fórmula: data_final_calculada = data_fim + prazos (dias)
          const dataFinalCalculada = new Date(dataFim);
          dataFinalCalculada.setDate(dataFinalCalculada.getDate() + diasPrazo);
          
          // Formatar para YYYY-MM-DD
          const dataFormatada = dataFinalCalculada.toISOString().split('T')[0];
          
          setFormData(prev => ({
            ...prev,
            data_final_calculada: dataFormatada
          }));
        }
      } catch (error) {
        logger.error('partnership', 'PartnershipForm.calculateDate', 'Erro ao calcular data final', { data_fim, prazos }, error);
      }
    } else {
      // Cenário B: Datas ausentes - limpar data_final_calculada
      if (!data_inicio || !data_fim) {
        setFormData(prev => ({
          ...prev,
          data_final_calculada: ''
        }));
      }
    }
  }, [formData.data_inicio, formData.data_fim, formData.prazos]);

  // Definir prazo padrão (90 dias) quando ambas as datas forem preenchidas
  useEffect(() => {
    const { data_inicio, data_fim, prazos } = formData;
    
    // Se ambas as datas estão preenchidas e prazo ainda não foi definido ou é "Não Calculado"
    if (data_inicio && data_fim && (!prazos || prazos === 'Não Calculado')) {
      // Buscar prazo padrão (90 dias)
      const prazoDefault = prazosDisponiveis.find(p => p.dias === 90);
      if (prazoDefault) {
        setFormData(prev => ({
          ...prev,
          prazos: prazoDefault.dias.toString()
        }));
      }
    }
    // Se uma das datas for removida, definir como "Não Calculado"
    else if ((!data_inicio || !data_fim) && prazos && prazos !== 'Não Calculado') {
      setFormData(prev => ({
        ...prev,
        prazos: 'Não Calculado'
      }));
    }
  }, [formData.data_inicio, formData.data_fim, prazosDisponiveis]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-preencher ano baseado na data de início
    if (name === 'data_inicio' && value) {
      const year = new Date(value).getFullYear();
      if (!isNaN(year)) {
        setFormData(prev => ({
          ...prev,
          ano: year
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Campos obrigatórios (processo_ano é opcional)
    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    }
    if (!formData.osc.trim()) {
      newErrors.osc = 'Nome da OSC é obrigatório';
    }

    // Validação de CNPJ (verificar se tem 14 dígitos)
    const cleanedCNPJ = cleanCNPJ(formData.cnpj);
    if (formData.cnpj && cleanedCNPJ.length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    // Validação de valor (se preenchido)
    if (formData.valor) {
      const numericValue = removeCurrencyMask(formData.valor);
      if (isNaN(numericValue) || numericValue < 0) {
        newErrors.valor = 'Valor deve ser um número válido e positivo';
      }
    }

    // Validação de ano
    if (formData.ano && (isNaN(parseInt(formData.ano)) || parseInt(formData.ano) < 1900 || parseInt(formData.ano) > 2100)) {
      newErrors.ano = 'Ano deve ser válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Preparar dados para envio
    const dataToSave = {
      ...formData,
      valor: formData.valor ? removeCurrencyMask(formData.valor) : null,
      ano: formData.ano ? parseInt(formData.ano) : null,
      data_inicio: formData.data_inicio || null,
      data_fim: formData.data_fim || null,
      tipo_parceria: formData.tipo_parceria || null,
      origem: formData.origem || 'Não Informado',
      prazos: formData.prazos || 'Não Calculado',
      data_final_calculada: formData.data_final_calculada || null,
    };

    onSave(dataToSave);
  };

  // Portal pro document.body: um modal fixed dentro do fluxo normal da página
  // herda margin/padding de containers pai (ex.: space-y-6), o que desloca o
  // fundo escurecido e deixa uma faixa sem cobrir no topo. Portal escapa desse
  // fluxo por completo, então o overlay sempre cobre o viewport de verdade.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden"
      >
        {/* Header padronizado com o resto do sistema (mesmo padrão do AuditDetailsModal:
            badge de ícone + título/subtítulo + botão de fechar circular) */}
        <div className="bg-gradient-to-r from-[#003098] to-[#0074f6] text-white p-4 sm:p-6 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold">
                {partnership ? 'Editar Parceria' : 'Nova Parceria'}
              </h2>
              <p className="text-xs sm:text-sm text-blue-100">
                {partnership ? (partnership.osc || 'Atualize os dados da parceria') : 'Preencha os dados da nova parceria'}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            aria-label="Fechar"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Processo/Ano */}
                <div className="space-y-2">
                  <Label htmlFor="processo_ano">
                    Processo/Ano
                  </Label>
                  <Input
                    id="processo_ano"
                    value={formData.processo_ano}
                    onChange={(e) => handleChange('processo_ano', e.target.value)}
                    placeholder="Ex: 12345/2024"
                    className={errors.processo_ano ? 'border-red-500' : ''}
                  />
                  {errors.processo_ano && (
                    <p className="text-sm text-red-500">{errors.processo_ano}</p>
                  )}
                </div>

                {/* Origem - Dinâmico */}
                <div className="space-y-2">
                  <Label htmlFor="origem">Origem</Label>
                  <Select
                    value={formData.origem}
                    onValueChange={(value) => handleChange('origem', value)}
                    disabled={loadingOptions}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingOptions ? "Carregando..." : "Selecione a origem"} />
                    </SelectTrigger>
                    <SelectContent>
                      {origens.map(origem => (
                        <SelectItem key={origem.id} value={origem.nome}>
                          {origem.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {origens.length} origem(ns) disponível(is)
                  </p>
                </div>
                
                {/* Tipo de Parceria - Dinâmico */}
                <div className="space-y-2">
                  <Label htmlFor="tipo_parceria">Tipo de Parceria</Label>
                  <Select
                    value={formData.tipo_parceria}
                    onValueChange={(value) => handleChange('tipo_parceria', value)}
                    disabled={loadingOptions}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingOptions ? "Carregando..." : "Selecione o tipo"} />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposParceria.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.nome}>
                          {tipo.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {tiposParceria.length} tipo(s) disponível(is)
                  </p>
                </div>

                {/* CNPJ */}
                <div className="space-y-2">
                  <Label htmlFor="cnpj">
                    CNPJ <span className="text-red-500">*</span>
                  </Label>
                  <CNPJInput
                    id="cnpj"
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={(e) => handleChange('cnpj', e.target.value)}
                    validateDigits={true}
                    showValidation={true}
                    required={true}
                    className={errors.cnpj ? 'border-red-500' : ''}
                  />
                  {errors.cnpj && (
                    <p className="text-sm text-red-500">{errors.cnpj}</p>
                  )}
                </div>

                {/* OSC */}
                <div className="space-y-2">
                  <Label htmlFor="osc">
                    Nome da OSC <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="osc"
                    value={formData.osc}
                    onChange={(e) => handleChange('osc', e.target.value)}
                    placeholder="Nome da Organização da Sociedade Civil"
                    className={errors.osc ? 'border-red-500' : ''}
                  />
                  {errors.osc && (
                    <p className="text-sm text-red-500">{errors.osc}</p>
                  )}
                </div>

                {/* Valor */}
                <div className="space-y-2">
                  <Label htmlFor="valor">Valor (R$)</Label>
                  <Input
                    id="valor"
                    type="text"
                    inputMode="numeric"
                    value={formData.valor}
                    onChange={(e) => {
                      const maskedValue = applyCurrencyMask(e.target.value);
                      handleChange('valor', maskedValue);
                    }}
                    onBlur={(e) => {
                      // Garante formatação completa ao sair do campo
                      const numericValue = removeCurrencyMask(e.target.value);
                      const formatted = formatCurrencyForInput(numericValue);
                      handleChange('valor', formatted);
                    }}
                    placeholder="0,00"
                    maxLength={20}
                    className={errors.valor ? 'border-red-500' : ''}
                  />
                  <p className="text-xs text-gray-500">
                    💵 Formatação automática: 1.234.567,89
                  </p>
                  {errors.valor && (
                    <p className="text-sm text-red-500">{errors.valor}</p>
                  )}
                </div>

                {/* Ano */}
                <div className="space-y-2">
                  <Label htmlFor="ano">Ano</Label>
                  <Input
                    id="ano"
                    type="number"
                    value={formData.ano}
                    onChange={(e) => handleChange('ano', e.target.value)}
                    placeholder="2024"
                    min="1900"
                    max="2100"
                    className={errors.ano ? 'border-red-500' : ''}
                  />
                  {errors.ano && (
                    <p className="text-sm text-red-500">{errors.ano}</p>
                  )}
                </div>

                {/* Data de Início */}
                <div className="space-y-2">
                  <Label htmlFor="data_inicio">Data de Início</Label>
                  <Input
                    id="data_inicio"
                    type="date"
                    value={formData.data_inicio}
                    onChange={(e) => handleChange('data_inicio', e.target.value)}
                  />
                </div>

                {/* Data de Fim */}
                <div className="space-y-2">
                  <Label htmlFor="data_fim">Data de Fim</Label>
                  <Input
                    id="data_fim"
                    type="date"
                    value={formData.data_fim}
                    onChange={(e) => handleChange('data_fim', e.target.value)}
                  />
                </div>

                {/* UG */}
                <div className="space-y-2">
                  <Label htmlFor="ug">Unidade Gestora</Label>
                  <Input
                    id="ug"
                    value={formData.ug}
                    onChange={(e) => handleChange('ug', e.target.value)}
                    placeholder="UG"
                  />
                </div>

                {/* Prazo - Condicional (visível apenas se ambas as datas estiverem preenchidas) */}
                {formData.data_inicio && formData.data_fim && (
                  <div className="space-y-2">
                    <Label htmlFor="prazos">
                      Prazo (dias)
                      <span className="text-blue-600 ml-1">*</span>
                    </Label>
                    <Select
                      value={formData.prazos}
                      onValueChange={(value) => handleChange('prazos', value)}
                      disabled={loadingOptions}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={loadingOptions ? "Carregando..." : "Selecione o prazo"} />
                      </SelectTrigger>
                      <SelectContent>
                        {prazosDisponiveis.map(prazo => (
                          <SelectItem key={prazo.id} value={prazo.dias.toString()}>
                            {prazo.dias} dias
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Prazo para cálculo da data final (padrão: 90 dias)
                    </p>
                    {formData.data_final_calculada && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                          <strong>Data Final Calculada:</strong> {new Date(formData.data_final_calculada).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Local F */}
                <div className="space-y-2">
                  <Label htmlFor="local_f">Local do Documento</Label>
                  <Input
                    id="local_f"
                    value={formData.local_f}
                    onChange={(e) => handleChange('local_f', e.target.value)}
                    placeholder="Local do arquivamento (deixe vazio para 'Não Informado')"
                  />
                </div>

                {/* Status é calculado automaticamente - não precisa campo no formulário */}
              </div>

              {/* Análises e Observações */}
              <div className="space-y-2">
                <Label htmlFor="analises_observacoes">Análises e Observações</Label>
                <Textarea
                  id="analises_observacoes"
                  value={formData.analises_observacoes}
                  onChange={(e) => handleChange('analises_observacoes', e.target.value)}
                  placeholder="Observações sobre a parceria..."
                  rows={4}
                />
              </div>

              {/* Botões */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </>
                  )}
                </Button>
              </div>
            </form>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default PartnershipForm;
